import React from 'react';
import { Card } from '../components/ui/card';
import { MapPin, Battery, Zap, Calendar } from 'lucide-react';
import SEO from '../components/SEO';

const Projects = () => {
  const projects = [
    {
      id: 1,
      title: 'Residential Solar & Battery Installation - Leppington',
      location: 'Leppington, NSW 2179',
      type: 'Residential',
      date: 'December 2024',
      system: '10kW Solar System + Sigenergy 15kWh Battery',
      description: 'Complete residential solar and battery installation featuring premium Sigenergy battery storage system with 10kW solar panel array.',
      image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxzb2xhciUyMHBhbmVsc3xlbnwwfHx8fDE3NjEyNTE3NzV8MA&ixlib=rb-4.1.0&q=85',
      specs: [
        '10kW Solar Panel System',
        'Sigenergy 15kWh Battery Storage',
        '97% Depth of Discharge',
        'Smart Energy Management',
        'Full Home Backup Power'
      ]
    },
    {
      id: 2,
      title: 'Commercial Solar Installation - Western Sydney',
      location: 'Campbelltown, NSW',
      type: 'Commercial',
      date: 'November 2024',
      system: '50kW Commercial Solar System',
      description: 'Large-scale commercial solar installation for local business, reducing energy costs by 75% with premium solar technology.',
      image: 'https://images.unsplash.com/photo-1726866492047-7f9516558c6e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxjb21tZXJjaWFsJTIwc29sYXIlMjBpbnN0YWxsYXRpb258ZW58MHx8fHwxNzYwMjI4NTEwfDA&ixlib=rb-4.1.0&q=85',
      specs: [
        '50kW Commercial Solar Array',
        'High-efficiency panels',
        'Smart monitoring system',
        'Expected 75% cost reduction',
        'ROI within 4-5 years'
      ]
    },
    {
      id: 3,
      title: 'Fox ESS Battery Upgrade - Liverpool',
      location: 'Liverpool, NSW',
      type: 'Battery Storage',
      date: 'October 2024',
      system: 'Fox ESS EQ4800 Battery System (18.64kWh)',
      description: 'Battery storage upgrade for existing solar system with Fox ESS EQ4800, providing reliable backup power and energy independence.',
      image: 'https://images.unsplash.com/photo-1681263576084-e054b2d89903?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHw0fHxzb2xhciUyMGJhdHRlcnklMjBzeXN0ZW18ZW58MHx8fHwxNzYxMjUxNzU5fDA&ixlib=rb-4.1.0&q=85',
      specs: [
        'Fox ESS EQ4800 - 4 Modules',
        '18.64kWh Total Capacity',
        '100% Depth of Discharge',
        'Seamless backup switching',
        '10-year warranty'
      ]
    }
  ];

  return (
    <>
      <SEO 
        title="Solar Installation Projects Sydney | Energy Spectrum Portfolio NSW"
        description="View Energy Spectrum solar installation projects across Sydney and NSW. Residential, commercial, and battery storage installations. Sigenergy, Fox ESS projects in Leppington and Western Sydney."
        keywords="solar installation projects Sydney, solar portfolio NSW, residential solar Sydney, commercial solar projects, battery installation examples"
      />
      <div className="projects-container">
        <section className="page-hero">
        <div className="container">
          <h1 className="display-medium">Our Projects</h1>
          <p className="body-large page-hero-description">
            Explore our recent solar and battery installations across NSW
          </p>
        </div>
      </section>

      <section className="projects-section">
        <div className="container">
          <div className="projects-intro">
            <h2 className="heading-2">Recent Installations</h2>
            <p className="body-medium">
              Every project showcases our commitment to quality, precision, and customer satisfaction. 
              From residential homes to commercial businesses, we deliver solar solutions that exceed expectations.
            </p>
          </div>

          <div className="projects-grid">
            {projects.map((project) => (
              <Card key={project.id} className="project-card">
                <div className="project-image">
                  <img src={project.image} alt={`${project.title} - ${project.system} installed by Energy Spectrum`} />
                  <div className="project-badge">{project.type}</div>
                </div>
                <div className="project-content">
                  <div className="project-meta">
                    <div className="project-meta-item">
                      <Calendar size={16} />
                      <span>{project.date}</span>
                    </div>
                    <div className="project-meta-item">
                      <MapPin size={16} />
                      <span>{project.location}</span>
                    </div>
                  </div>
                  <h3 className="heading-3">{project.title}</h3>
                  <p className="body-medium project-system">
                    <Zap size={18} /> {project.system}
                  </p>
                  <p className="body-medium project-description">{project.description}</p>
                  
                  <div className="project-specs">
                    <h4 className="body-medium" style={{ fontWeight: 600, marginBottom: '12px' }}>Key Features:</h4>
                    <ul className="project-specs-list">
                      {project.specs.map((spec, index) => (
                        <li key={index} className="project-spec-item">
                          <Battery size={16} />
                          <span>{spec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          <div className="projects-coming-soon">
            <Card className="coming-soon-card">
              <h3 className="heading-2">More Projects Coming Soon</h3>
              <p className="body-large">
                We are continuously completing new installations across NSW. Check back regularly 
                to see our latest projects and how we are helping Australians transition to clean energy.
              </p>
            </Card>
          </div>
        </div>
      </section>
      </div>
    </>
  );
};

export default Projects;
